/** The self-registration links' server state (plan B2/B3): the query keys, the two counts
 *  the segmented filter shows, and the immediate actions every list shares (restore,
 *  make master) with their outcome in a snackbar. Every write invalidates the whole
 *  `['tours', id, 'selfreg-links']` family, so both lists and both counts follow. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { TourDetail, TourSelfregLink, TourSelfregLinkError } from '@/api/tours';
import { useSnackbar } from '@/components/Snackbar';

import { addressTail } from './linkWords';

export const selfregLinksKey = (tourId: number) => ['tours', tourId, 'selfreg-links'] as const;
export const tourWideKey = (tourId: number, status: 'active' | 'all') => [...selfregLinksKey(tourId), 'undated', status] as const;
export const datedKey = (tourId: number) => [...selfregLinksKey(tourId), 'dated'] as const;
export const countKey = (tourId: number, kind: 'undated' | 'dated') => [...selfregLinksKey(tourId), 'count', kind] as const;

/** The tour-wide list is one request: a tour has a handful of undated links. Past this a
 *  muted line says how many are not shown. */
export const TOUR_WIDE_PAGE_SIZE = 100;
/** The date-specific grid's page. */
export const DATED_PAGE_SIZE = 25;

/** The API's error code, if the error carries one. */
export function errorCode(e: unknown): TourSelfregLinkError['code'] | null {
  if (!(e instanceof ConsoleApiError)) return null;
  const code = (e.body as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? (code as TourSelfregLinkError['code']) : null;
}

export function errorText(e: unknown, fallback: string): string {
  return e instanceof ConsoleApiError && e.message ? e.message : fallback;
}

/** Active tour-wide · active date-specific: the detail's counts at first, re-read after
 *  any write (no request until then). */
export function useLinkCounts(detail: TourDetail, enabled: boolean): { tourWide: number; dated: number } {
  const count = (kind: 'undated' | 'dated', initial: number) => ({
    queryKey: countKey(detail.id, kind),
    queryFn: async () => (await toursApi.listSelfregLinks(detail.id, { kind, status: 'active', page_size: 1 })).count,
    initialData: initial,
    staleTime: Infinity,
    enabled,
  });
  const tourWide = useQuery(count('undated', detail.selfreg_link_count ?? 0));
  const dated = useQuery(count('dated', detail.selfreg_dated_link_count ?? 0));
  return { tourWide: tourWide.data, dated: dated.data };
}

/** Restore and Make master now: immediate, audited server-side; the outcome (or the
 *  failure, with Retry) in the snackbar. */
export function useLinkActions(tourId: number) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const refresh = () => qc.invalidateQueries({ queryKey: selfregLinksKey(tourId) });

  const restore = useMutation({
    mutationFn: (link: TourSelfregLink) => toursApi.restoreSelfregLink(tourId, link.id),
    onSuccess: (r, link) => {
      void refresh();
      const tail = addressTail(link);
      if (!r.restored) snackbar.show(`Link ${tail} was not revoked.`, 'info');
      else if (r.restored_as === 'master') snackbar.show(`Link ${tail} restored — it is the master link again.`, 'success');
      else if (link.kind === 'master') snackbar.show(`Link ${tail} restored as a custom link; the master stays as it is.`, 'success');
      else snackbar.show(`Link ${tail} restored — it works again.`, 'success');
    },
    onError: (e, link) => {
      void refresh();
      snackbar.show(
        errorCode(e) === 'master_conflict'
          ? 'The tour’s master link changed meanwhile — nothing was restored.'
          : errorText(e, 'Could not restore the link.'),
        'error',
        { action: { label: 'Retry', onClick: () => restore.mutate(link) } },
      );
    },
  });

  const makeMaster = useMutation({
    mutationFn: (link: TourSelfregLink) => toursApi.makeSelfregLinkMaster(tourId, link.id),
    onSuccess: (r, link) => {
      void refresh();
      snackbar.show(
        r.master_changed
          ? `Link ${addressTail(link)} is now the master link: notifications send it.`
          : `Link ${addressTail(link)} already is the master link.`,
        'success',
      );
    },
    onError: (e, link) => {
      void refresh();
      const code = errorCode(e);
      snackbar.show(
        code === 'master_conflict'
          ? 'The tour’s master link changed meanwhile — nothing was changed.'
          : code === 'not_eligible'
            ? `Link ${addressTail(link)} can no longer become the master (only an active link without a date can).`
            : errorText(e, 'Could not change the master link.'),
        'error',
        code === 'not_eligible' ? undefined : { action: { label: 'Retry', onClick: () => makeMaster.mutate(link) } },
      );
    },
  });

  return { restore, makeMaster, refresh };
}

/** Copy a link to the clipboard, saying so. */
export function useCopyLink() {
  const snackbar = useSnackbar();
  return (link: Pick<TourSelfregLink, 'id' | 'link'>) =>
    void navigator.clipboard.writeText(link.link).then(
      () => snackbar.show(`Copied link ${addressTail(link)}`, 'success'),
      () => snackbar.show('Copy failed — clipboard unavailable', 'error'),
    );
}
